import * as React from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import { parseISO, format, isValid } from "date-fns";
import { Calendar, AlertCircle } from "lucide-react";

import { cn } from "../lib/utils";
import { Label } from "./ui/label";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("ptBR", ptBR);

interface DatePickerFieldProps {
  label?: string;
  value?: string; // formato YYYY-MM-DD
  onChange: (value: string) => void; // retorna YYYY-MM-DD
  required?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  error?: string;
  className?: string;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  label,
  value,
  onChange,
  required,
  disabled,
  id,
  name,
  error,
  className,
  placeholder,
  minDate,
  maxDate,
}) => {
  const fieldId =
    id || name || `datepicker-${Math.random().toString(36).substr(2, 9)}`;

  const selectedDate = React.useMemo(() => {
    if (!value) return null;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  }, [value]);

  const handleChange = (date: Date | null) => {
    onChange(date ? format(date, "yyyy-MM-dd") : "");
  };

  return (
    <div className="space-y-1">
      {label && (
        <Label
          htmlFor={fieldId}
          className={cn(
            "text-sm font-medium text-muted-foreground",
            error && "text-red-600 dark:text-red-400"
          )}
        >
          {label}
          {required && (
            <span className="text-red-500 dark:text-red-400 ml-1">*</span>
          )}
        </Label>
      )}
      <div className="relative">
        <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary pointer-events-none z-10" />
        <DatePicker
          id={fieldId}
          name={name}
          selected={selectedDate}
          onChange={handleChange}
          locale="ptBR"
          dateFormat="dd/MM/yyyy"
          disabled={disabled}
          placeholderText={placeholder || "DD/MM/AAAA"}
          minDate={minDate}
          maxDate={maxDate}
          className={cn(
            "flex h-10 w-full rounded-md border border-border bg-background text-foreground pl-10 pr-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-red-300 dark:border-red-800 focus:ring-red-500",
            className
          )}
          wrapperClassName="w-full"
          autoComplete="off"
        />
      </div>
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

DatePickerField.displayName = "DatePickerField";

export { DatePickerField };
